import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAresLookup, useAresSearch } from '../../api/hooks';
import type { AresDetail, AresSearchItem } from '../../api/schemas';
import type { AresPickResult } from '../ares/AresSearchField';
import styles from './DocInlineAres.module.css';
import { cx } from '../../lib/cx';

function formatAdresa(d: AresDetail): string {
  const first = [d.ulice].filter(Boolean).join(' ');
  const second = [d.psc, d.mesto].filter(Boolean).join(' ');
  return [first, second].filter(Boolean).join(', ');
}

export interface DocInlineAresNameProps {
  value: string;
  onChange: (value: string) => void;
  onPick: (data: AresPickResult) => void;
  placeholder?: string;
}

/**
 * Inline ARES whisperer pro hodnotu řádku „Firma" v dokumentu.
 * Vykresluje se jako prostý input + autocomplete popover (objeví se na focus).
 */
export function DocInlineAresName({
  value,
  onChange,
  onPick,
  placeholder = 'Stavební firma s.r.o.',
}: DocInlineAresNameProps) {
  const [debounced, setDebounced] = useState(value);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Název firmy je textarea s jedním řádkem, která roste s obsahem — dlouhý
  // název (SVJ, příspěvková organizace…) se zalomí místo oříznutí v inputu.
  // Enter se ignoruje, nové řádky se do hodnoty nedostanou.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  // Popover pozicujeme `position: fixed` podle inputu — jinak ho ořezává
  // `overflow: hidden` na obalu řádků (`.table`), který je tam kvůli zaobleným rohům.
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const updatePos = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const onMove = () => updatePos();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, updatePos]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(t);
  }, [value]);

  const searchQuery = useAresSearch(debounced);
  const [lookupIco, setLookupIco] = useState<string | null>(null);
  const lookupQuery = useAresLookup(lookupIco, { enabled: !!lookupIco });

  useEffect(() => {
    if (lookupQuery.isSuccess && lookupQuery.data) {
      const d = lookupQuery.data;
      onPick({ ...d, adresa: formatAdresa(d) });
      onChange(d.nazev);
      setLookupIco(null);
    }
  }, [lookupQuery.isSuccess, lookupQuery.data, onChange, onPick]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const suggestions = useMemo<AresSearchItem[]>(() => {
    if (!open) return [];
    if (searchQuery.isFetching) return [];
    return searchQuery.data ?? [];
  }, [open, searchQuery.isFetching, searchQuery.data]);

  return (
    <span className={styles.nameWrap} ref={wrapRef}>
      <textarea
        ref={inputRef}
        rows={1}
        className={styles.nameInput}
        value={value}
        onChange={(e) => {
          onChange(e.target.value.replace(/[\r\n]+/g, ' '));
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
        onFocus={() => value.length >= 2 && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && value.trim().length >= 2 && (
        <div
          className={cx(styles.suggestList, 'docNoPrint')}
          role="listbox"
          style={pos ? { top: pos.top, left: pos.left, width: pos.width } : undefined}
        >
          {searchQuery.isFetching && <div className={styles.suggestMsg}>Hledám…</div>}
          {!searchQuery.isFetching && suggestions.length === 0 && (
            searchQuery.isError ? (
              <div className={cx(styles.suggestMsg, styles.error)}>⚠ Chyba ARES.</div>
            ) : (
              <div className={styles.suggestMsg}>Žádné výsledky.</div>
            )
          )}
          {suggestions.map((s) => (
            <button
              key={s.ico}
              type="button"
              className={styles.suggestItem}
              onClick={() => {
                setOpen(false);
                setLookupIco(s.ico);
              }}
            >
              <div className={styles.suggestName}>{s.nazev}</div>
              <div className={styles.suggestDetail}>
                IČO <span className={styles.mono}>{s.ico}</span> · {s.adresa}
              </div>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

export interface DocInlineAresLookupProps {
  ico: string;
  onResult: (data: AresPickResult) => void;
  /** Tooltip pro tlačítko. */
  title?: string;
}

/**
 * Tlačítko „🔍 ARES" — vyhledá detail podle IČO a propíše do dokumentu.
 * Vkládá se do `actions=` slotu DocRow s IČO.
 */
export function DocInlineAresLookup({
  ico,
  onResult,
  title = 'Načíst z ARES podle IČO',
}: DocInlineAresLookupProps) {
  const [active, setActive] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const lookupQuery = useAresLookup(active, { enabled: !!active });

  useEffect(() => {
    if (lookupQuery.isError) {
      setErr('Nenalezeno');
      setActive(null);
      const t = setTimeout(() => setErr(null), 2200);
      return () => clearTimeout(t);
    }
    if (lookupQuery.isSuccess && lookupQuery.data) {
      onResult({ ...lookupQuery.data, adresa: formatAdresa(lookupQuery.data) });
      setActive(null);
    }
  }, [lookupQuery.isError, lookupQuery.isSuccess, lookupQuery.data, onResult]);

  function trigger() {
    setErr(null);
    const cleaned = ico.replace(/\s+/g, '');
    if (!/^\d{6,8}$/.test(cleaned)) {
      setErr('⚠ neplatné IČO');
      const t = setTimeout(() => setErr(null), 2200);
      return () => clearTimeout(t);
    }
    setActive(cleaned);
  }

  const loading = lookupQuery.isFetching && !!active;

  return (
    <span className={styles.lookupWrap}>
      <button
        type="button"
        className={styles.lookupBtn}
        onClick={trigger}
        disabled={loading}
        title={title}
        aria-label={title}
      >
        {loading ? '⏳' : '🔍 ARES'}
      </button>
      {err && <span className={styles.lookupErr}>{err}</span>}
    </span>
  );
}
